import { Component, HostListener, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizationAwareComponent } from '../../../core/authorization-aware-component';
import { AppState } from '../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { StoreComparison } from '../../../core/store/ranking/store-comparison';
import { StoreComparisonService } from '../../../core/store/ranking/store-comparison.service';
import { StoreRankingService } from '../../../core/store/ranking/store-ranking.service';
import { FeatureCollection, Store } from '../../../core/models';
import { DetailState, selectDetailState } from '../../detail-state';
import { filter } from 'rxjs/operators';
import { KeyIndicatorType } from '../../../core/user-preferences/key-indicator-type.enum';
import { RegionType } from '../../../core/location/region-type.enum';
import * as _ from 'lodash';
import { ComparisonType } from '../../../core/store/ranking/comparison-type.enum';
import { StoreVolume } from '../../../core/store/volume/store-volume';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RankingFilters } from './store-ranking-filters/store-ranking-filters.component';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

/** GRO-713: one absolute-rank ring (e.g. "2 of 62" for State). */
interface RankRing {
  label: string;
  rank: number | null;
  total: number | null;
  color: string;
  dash: string;
}

/** GRO-713: a ring group — "{banner} vs {banner}" or "{banner} vs All Grocers". */
interface RankRingGroup {
  title: string;
  accent: string;
  rings: RankRing[];
  /** GRO-713/715: "vs All Grocers" also auto-opens the store detail popup on click. */
  autoOpenDetail: boolean;
}

@Component({
  standalone: false,
  selector: 'mtn-store-sales-performance-panel',
  templateUrl: './store-sales-performance-panel.component.html',
  styleUrls: ['./store-sales-performance-panel.component.scss']
})
export class StoreSalesPerformancePanelComponent extends AuthorizationAwareComponent {

  // GRO-713/715: six absolute-rank rings (2 groups × 3), replacing the percentile rings.
  rankGroups: RankRingGroup[] = [];
  ringsLoading = false;
  private readonly RING_CIRCUMFERENCE = 2 * Math.PI * 42; // r=42 donut
  @Output() showComprehensiveRanking = new EventEmitter<void>();
  @Output() keyIndicatorChanged = new EventEmitter<'volume' | 'sqft'>();
  @Output() rankingFiltersForPdf = new EventEmitter<RankingFilters>();
  @Output() rankingDataForPdf = new EventEmitter<any[]>();

  selectedComparisons: StoreComparison[] = [];

  comparisonRequest1A: StoreComparison;
  comparisonRequest1B: StoreComparison;
  comparisonRequest1C: StoreComparison;
  comparisonRequest2A: StoreComparison;
  comparisonRequest2B: StoreComparison;
  comparisonRequest2C: StoreComparison;
  store: Store;
  volume: StoreVolume;
  comparisonTypeForm = new UntypedFormGroup({
    keyIndicator: new UntypedFormControl(null, [Validators.required]),
  });
  marketName: any;

  private _isInitialized = false;
  private _isKeyIndicatorSubscribed = false;
  private _marketArea: FeatureCollection;
  keyIndicatorDropdownOpen = false;

  // Store ranking data
  currentComparisonType: ComparisonType = ComparisonType.ALL;
  currentRankingFilters: RankingFilters;

  // Make enum available in template
  readonly KeyIndicatorType = KeyIndicatorType;
  readonly ComparisonType = ComparisonType;

  // ... your existing methods

  toggleKeyIndicatorDropdown(): void {
    this.keyIndicatorDropdownOpen = !this.keyIndicatorDropdownOpen;
  }

  selectKeyIndicator(keyIndicator: KeyIndicatorType): void {
    this.comparisonTypeForm.get('keyIndicator')?.setValue(keyIndicator);
    this.keyIndicatorDropdownOpen = false;
    let comparisontype= 'na';
    if(keyIndicator === KeyIndicatorType.SALES_VOLUME) {
      comparisontype='Estimated Sales Volume';
    } else if(keyIndicator === KeyIndicatorType.SALES_SQFT) {
      comparisontype='Estimated Sales/SQFT';
    }
    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
      comparisonType: comparisontype,
    };

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
      metadata,
      USER_TRACKING_ACTIONS.COMPARISON_CHANGED,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.COMPARISON_CHANGED]
    ).subscribe();
  }

  getSelectedKeyIndicatorLabel(): string {
    const selectedValue = this.comparisonTypeForm.get('keyIndicator')?.value;

    switch (selectedValue) {
      case KeyIndicatorType.SALES_SQFT:
        return 'Estimated Sales/SQFT'; // Or use your translation pipe if needed
      case KeyIndicatorType.SALES_VOLUME:
        return 'Estimated Sales Volume'; // Or use your translation pipe if needed
      default:
        return 'Select Key Indicator';
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  // Close dropdown when clicking outside (optional)
  @HostListener('document:click', ['$event'])
  onDocumentClick(_event: Event): void {
    this.keyIndicatorDropdownOpen = false;
  }

  constructor(protected ngrxStore: NgrxStore<AppState>,
    public hitTrackingService: HitTrackingService,
    private storeComparisonService: StoreComparisonService,
    private storeRankingService: StoreRankingService,
    private router: Router
              //private wizardRunnerService: WizardRunnerService
              ) {
    super(ngrxStore);
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
    this.loadStore();
    this.initComparisonRequests();
  }
 private subscribeToKeyIndicatorChanges(): void {
  this.addSubscription(
    this.comparisonTypeForm.get('keyIndicator').valueChanges
      .subscribe((value: KeyIndicatorType) => {
        if (value === KeyIndicatorType.SALES_VOLUME) {
          this.selectedComparisons = [
            this.comparisonRequest1A,
            this.comparisonRequest1B,
            this.comparisonRequest1C,
          ];
          // Emit the change for PDF export
          this.keyIndicatorChanged.emit('volume');
        } else if (value === KeyIndicatorType.SALES_SQFT) {
          this.selectedComparisons = [
            this.comparisonRequest2A,
            this.comparisonRequest2B,
            this.comparisonRequest2C,
          ];
          // Emit the change for PDF export
          this.keyIndicatorChanged.emit('sqft');
        } else {
          this.selectedComparisons = [];
        }
      })
  );
}


  private initComparisonRequests(): void {
    const defaultComparisons = this.storeComparisonService.buildDefaultComparisons(this.store, this.volume, this._marketArea, this.userPreferences);

    this.comparisonRequest1A = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_VOLUME && comparison.region === RegionType.DRIVE_TIME;
    });
    this.comparisonRequest1B = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_VOLUME && comparison.region === RegionType.MARKET_AREA && comparison.comparisonType === ComparisonType.ALL;
    });
    this.comparisonRequest1C = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_VOLUME && comparison.region === RegionType.MARKET_AREA && comparison.comparisonType === ComparisonType.BANNER;
    });

    this.comparisonRequest2A = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_SQFT && comparison.region === RegionType.DRIVE_TIME;
    });
    this.comparisonRequest2B = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_SQFT && comparison.region === RegionType.MARKET_AREA && comparison.comparisonType === ComparisonType.ALL;
    });
    this.comparisonRequest2C = _.find(defaultComparisons, (comparison: StoreComparison) => {
      return comparison.keyIndicator === KeyIndicatorType.SALES_SQFT && comparison.region === RegionType.MARKET_AREA && comparison.comparisonType === ComparisonType.BANNER;
    });

    this.comparisonTypeForm.get('keyIndicator').setValue(this.userPreferences.primaryKeyIndicator, { emitEvent: true });
    if (!this._isKeyIndicatorSubscribed) {
      this._isKeyIndicatorSubscribed = true;
      this.subscribeToKeyIndicatorChanges();
    }
  }

  private loadStore(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .pipe(filter((state: DetailState) => !!state.store))
        .subscribe((state: DetailState) => {
          this.store = state.store;
          const feature = state.marketArea?.getFeature();
          this.marketName = feature?.properties ? feature.properties['name'] : null;
          this.volume = state.volume;
          this._marketArea = state.marketArea;

          if (this.store && !this.store.isFuture() && this.volume && !this._isInitialized) {
            this._isInitialized = true;
            this.initComparisonRequests();
            this.loadRankRings();
          }
        })
    );
  }

  /**
   * GRO-713: loads the six absolute ranks for THIS store from its own row in the
   * comprehensive-ranking response (which already carries banner + industry ranks
   * for drive-time / CBSA / state). Scoped to the store's state so the row lands on
   * the first page; falls back to N/A rings if the row can't be found.
   */
  private loadRankRings(): void {
    const banner = this.store?.banner;
    const location = this.store?.space?.location;
    if (!banner || !location) {
      this.rankGroups = this.buildRankGroups(null);
      return;
    }

    const filter: any = {};
    if (banner.id) {
      filter.bannerIds = [banner.id];
    }
    if (location.state) {
      // Narrow the result set so this store's row is on the first page (the rank
      // values themselves are absolute/precomputed and unaffected by the filter).
      filter.states = [location.state];
    }

    const payload: any = {
      driveTime: 7,
      miles: 5,
      salesVolumeDisplayType: 'WEEKLY',
      longitude: location.getPointLongitude(),
      latitude: location.getPointLatitude(),
      storeId: this.store.id,
    };
    if (Object.keys(filter).length > 0) {
      payload.filter = filter;
    }

    this.ringsLoading = true;
    this.addSubscription(
      this.storeRankingService.getComprehensiveRanking(payload, 0, 1000).subscribe({
        next: (res: any) => {
          const rows: any[] = res?.data ?? res?.content ?? (Array.isArray(res) ? res : []);
          const row = rows.find((x: any) => x.store_uuid === this.store?.uuid) ?? null;
          this.rankGroups = this.buildRankGroups(row);
          this.ringsLoading = false;
        },
        error: () => {
          this.rankGroups = this.buildRankGroups(null);
          this.ringsLoading = false;
        },
      })
    );
  }

  private buildRankGroups(r: any): RankRingGroup[] {
    const name = this.store?.banner?.name ?? 'This Banner';
    const BANNER_ACCENT = '#F4742F';
    const INDUSTRY_ACCENT = '#3E82DA';
    const ring = (label: string, rank: any, total: any, color: string): RankRing => ({
      label,
      rank: rank ? Number(rank) : null,
      total: total ? Number(total) : null,
      color,
      dash: this.dashFor(rank, total),
    });

    return [
      {
        title: `${name} vs ${name}`,
        accent: BANNER_ACCENT,
        autoOpenDetail: false,
        rings: [
          ring('7-min drive', r?.drive_time_banner, r?.total_count_banner_drive_time, BANNER_ACCENT),
          ring('CBSA', r?.cbsa_rank_banner, r?.total_count_banner_cbsa, BANNER_ACCENT),
          ring('State', r?.state_rank_banner, r?.total_count_banner_state, BANNER_ACCENT),
        ],
      },
      {
        title: `${name} vs All Grocers`,
        accent: INDUSTRY_ACCENT,
        autoOpenDetail: true,
        rings: [
          ring('7-min drive', r?.drive_time_industry, r?.total_count_industry_drive_time, INDUSTRY_ACCENT),
          ring('CBSA', r?.cbsa_rank_industry, r?.total_count_cbsa, INDUSTRY_ACCENT),
          ring('State', r?.state_rank_industry, r?.total_count_state, INDUSTRY_ACCENT),
        ],
      },
    ];
  }

  /**
   * Arc length for a ring — fill reflects rank QUALITY, not rank/total.
   * GRO-713 (PO fix): fill = 1 - ((rank - 1) / total), so being closer to #1 fills
   * the ring more (rank 1 → full; worse ranks → emptier). Denominator is `total`
   * (not total - 1) so a "1 of 1" ring doesn't divide by zero; the very last rank
   * therefore floors at ~1/total rather than exactly 0. Min 6% so a sliver always shows.
   * e.g. 1/1 → 100%, 9/10 → 20%, 15/111 → ~87%, 37/590 → ~94%.
   */
  private dashFor(rank: any, total: any): string {
    const r = Number(rank);
    const t = Number(total);
    const frac = (!r || !t) ? 0 : 1 - ((r - 1) / t);
    const filled = Math.min(1, Math.max(0.06, frac)) * this.RING_CIRCUMFERENCE;
    return `${filled.toFixed(1)} ${this.RING_CIRCUMFERENCE.toFixed(1)}`;
  }

  onRankingFiltersChanged(filters: RankingFilters): void {
    this.currentComparisonType = filters.comparisonType;
    this.currentRankingFilters = filters;
    
    // Emit filters to parent component for PDF generation
    this.rankingFiltersForPdf.emit(filters);
  }

  /**
   * Receive ranking data from scatter chart component
   */
  onRankingDataUpdated(data: any[]): void {
    this.rankingDataForPdf.emit(data);
  }

  onShowComprehensiveRanking(autoOpenStoreDetail = false): void {
    const metadata = {
      storeId: this.store?.uuid,
      storeName: this.store?.name,
    };

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
      metadata,
      USER_TRACKING_ACTIONS.COMPREHENSIVE_CLICKED,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.COMPREHENSIVE_CLICKED]
    ).subscribe();

    // Open comprehensive ranking in a new tab. When autoOpenStoreDetail is true
    // (the "vs All Grocers" ring), pass ?openStoreDetail=1 so the ranking page
    // auto-opens the selected store's Location Details popup once its data loads.
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/comprehensive-ranking', this.store?.uuid],
        autoOpenStoreDetail ? { queryParams: { openStoreDetail: 1 } } : {}
      )
    );
    window.open(url, '_blank');
  }

  // showComprehensiveRanking(): void {
  //   const wizard = new ComprehensiveRankingTableWizard();
  //   wizard.model = {
  //     store: this.store
  //   };
  //   this.wizardRunnerService.run(wizard);
  // }

  trackComparisonCardClick(request: StoreComparison): void {
    const store = request.store;
    const location = store?.space?.location;
    const shoppingCenter = store?.space?.shoppingCenter;

    const metadata = {
      storeId: store?.uuid,
      storeName: store?.name,
      addressLine1: location?.addressLine1,
      city: location?.city,
      cbsa: this.marketName,
      state: location?.state,
      postalCode: location?.postalCode,
      county: location?.county,
      shoppingCenterName: shoppingCenter?.name,
      landlord: shoppingCenter?.ownerCompany?.name
    };

    const keyIndicator = this.toCamelCase(request.keyIndicator);
    const comparisonType = this.toCamelCase(request.comparisonType);
    const region = this.toCamelCase(request.region);

    const description = `Clicked Comparison Card: ${keyIndicator} vs ${comparisonType} in ${region}`;

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
      metadata,
      USER_TRACKING_ACTIONS.CLICK_EVENT,
      description
    ).subscribe();
  }
  /**
   * Converts uppercase snake_case to camelCase
   * Example: SALES_VOLUME → salesVolume, DRIVE_TIME → driveTime
   */
  toCamelCase(value: string): string {
    return value
      .toLowerCase()
      .split('_')
      .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  trackByComparison(_index: number, comparison: StoreComparison): string {
    // console.log("🚀 ~ StoreSalesPerformancePanelComponent ~ trackByComparison ~ index:", index)
    // Create unique key based on keyIndicator, comparisonType, and region
    return `${comparison.keyIndicator}-${comparison.comparisonType}-${comparison.region}`;
  }



}
